import { useQuery } from '@tanstack/react-query'
import { ApiError, api } from './api'

export interface CurrentUser {
  userId: string
  email: string | null
  displayName: string | null
  isSiteAdmin: boolean
}

/** Public bootstrap values from the API. Safe to expose in a browser by design. */
export interface PublicConfig {
  googleClientId: string
  mapsApiKey: string
  /** False when the deployment has no OAuth credentials, so sign-in cannot work yet. */
  googleSignInEnabled: boolean
}

/**
 * The signed-in staff user, or null when nobody is signed in.
 *
 * A 401 is a normal answer here, not an error, so it resolves to null rather than
 * throwing and putting the query into a retry loop.
 */
export function useCurrentUser() {
  return useQuery({
    queryKey: ['current-user'],
    queryFn: async () => {
      try {
        return await api.get<CurrentUser>('/api/me')
      } catch (error) {
        if (error instanceof ApiError && error.isUnauthenticated) {
          return null
        }
        throw error
      }
    },
    staleTime: 5 * 60 * 1000,
    retry: false,
  })
}

export function usePublicConfig() {
  return useQuery({
    queryKey: ['public-config'],
    queryFn: () => api.get<PublicConfig>('/api/config'),
    // Changes only on deploy.
    staleTime: Infinity,
  })
}
